import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, SubmissionError } from 'redux-form';
import urlJoin from 'url-join';

import actions from 'actions';
import {
    AVERAGE_LIGHTNESS,
    AVERAGE_PEACEFULNESS,
    AVERAGE_RELATIVE_PERCEPTION,
    AVERAGE_RENOVATION_LEVEL,
    AVERAGE_ROOF,
    AVERAGE_STANDING,
    AVERAGE_TRANSPORT_PROXIMITY,
    BAROMETRE_URL,
    ESTIMA_FORM,
    FORM_ADDRESS_NAME,
    FORM_CITY_NAME,
    FORM_ZIP,
    GROUND_GARDEN_OPTIONS,
    LOGIN_INPUT_PREFIX,
    ORIGIN_ESTIMA_FUNNEL,
    SIGNUP_SUCCEEDED,
    STEP_ID_NAME,
    STEP_NUMBER_NAME,
} from 'constants/Global';
import {
    extractLoginValues,
    isBarometreStepNumber,
    scrollToFirstError,
} from 'helpers/FormInformations';
import { getSearchParamsAsObject } from 'helpers/Referrer';
import { isOfflineWithToken } from 'helpers/Estima';
import AuthService from 'services/AuthService';
import EstimaFormService from 'services/EstimaFormService';
import { STEP_IDS } from 'constants/Steps';
import { triggerAudienceTag } from 'helpers/AudienceTag';
import { INTERACTION_EVENTS_LIST, thorPushEvent } from '../helpers/Ga4';

/**
 * AppComponent Component.
 */
export class AppComponent extends Component {
    /**
     * Creates an instance of AppComponent.
     * @param {Object} props Component props
     */
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
    }

    /**
     * React lifecycle.
     */
    componentDidMount() {
        const params = getSearchParamsAsObject();

        let onFullFill = actions.stepsReady();
        if (new RegExp(`${BAROMETRE_URL}$`).test(document.location.pathname)) {
            onFullFill = actions.barometreReady();
        }

        if (window.estima) {
            this.props.setEstima(window.estima);
            this.props.dispatch(onFullFill);
        } else if (params.estima_id) {
            this.props.getEstima(params.estima_id, onFullFill);
        }
    }

    /**
     * Submit estima creation and form
     * @return {[type]} [description]
     */
    submit() {
        this.props.dispatch(actions.createEstima());
        this.props.submit();
    }

    /**
     * React lifecycle.
     */
    render() {
        const { children, ...rest } = this.props;

        if (children) {
            return React.cloneElement(children, {
                ...rest,
                submit: this.submit,
            });
        }

        return null;
    }
}

AppComponent.propTypes = {
    children: PropTypes.node,
};

/**
 * Check if current form state is login step
 * @param  {Object}  values Current form state
 * @return {Boolean}        True if login step, false otherwise.
 */
function isLoginStep(values) {
    return values[STEP_ID_NAME] === STEP_IDS.login;
}

/**
 * Check if current form state is retrieve phone step
 * @param  {Object}  values Current form state
 * @return {Boolean}        True if retrieve phone step, false otherwise.
 */
function isRetrievePhoneStep(values) {
    return values[STEP_ID_NAME] === STEP_IDS.retrievePhone;
}

/**
 * Handle remote error for estima.
 * @param {Object} error Error response from remote
 */
const handleEstimaError = (error, dispatch, values) => {
    if (error.response && error.response.status === 400) {
        if (!isBarometreStepNumber(values[STEP_NUMBER_NAME])) {
            scrollToFirstError();
        }

        // Displaying error information if on login step
        if (isLoginStep(values)) {
            dispatch(actions.setError(error));
        }

        const responseData = error.response.data;
        throw Object.keys(responseData).reduce((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current] = [].concat(responseData[current]).join('\n');
            return previous;
        }, {});
    } else {
        const formError = {
            _error: JSON.stringify(error),
            context: 'estima form submit request responded with an unexpected error format',
        };
        dispatch(actions.setError(formError));
        throw formError;
    }
};

/**
 * Submit estima form with error handling.
 * @param {Object} values EstimaForm values
 * @param {Function} dispatch Redux dispatch method
 */
const estimaSubmit = (values, dispatch) => {
    EstimaFormService.submit(values)
        .then(({ data }) => {
            // ⚠️ Don't dispatch received event for keeping Throbber in submit button ⚠️
            // dispatch(actions.createEstimaReceived());

            thorPushEvent({
                eventType: INTERACTION_EVENTS_LIST.submitEstimationSuccess,
                payload: { form: values },
            });
            triggerAudienceTag(values);
            if (data.redirect) {
                // rate limiter redirect response
                window.location.assign(urlJoin(MA.Config.WWW_URL, data.redirect));
            }

            dispatch(actions.saveEstimaId(data.habitation_id, data.signed_username));

            return true;
        })
        .catch((error) => {
            dispatch(actions.createEstimaReceived()); // Reset pending status of submit without showing error
            handleEstimaError(error, dispatch, values);
        });
};

/**
 * Handle remote error for login.
 * @param {Object} error Error response from remote
 */
const handleAuthError = (error, dispatch) => {
    let formError;

    if (error.response && error.response.status === 400) {
        const responseData = error.response.data;
        formError = Object.keys(responseData).reduce((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[`${LOGIN_INPUT_PREFIX}${current}`] = []
                .concat(responseData[current])
                .join('\n');
            return previous;
        }, {});

        scrollToFirstError();
    } else {
        formError = {
            _error: JSON.stringify(error),
        };
        dispatch(actions.setError(formError));
    }

    dispatch(actions.createEstimaReceived()); // Reset pending status of submit without showing error
    dispatch(actions.checkAuth());
    throw new SubmissionError(formError);
};

/**
 * Validation callback for async validation.
 * ⚠️ Redux-form consider any resolved value as an `asyncErrors`. So if everything is fine, you should resolve true.
 * @param {Object} values Form input data
 */
const asyncValidate = (values, dispatch) => {
    // Removing validation on last step, blocking signin/signup
    if (isLoginStep(values)) {
        return Promise.resolve({});
    }

    return EstimaFormService.validate(values)
        .then(() => true)
        .catch((error) => {
            dispatch(actions.createEstimaReceived());
            return handleEstimaError(error, dispatch, values);
        });
};

/**
 * Update contact information and submit estima on success
 * @param {Object} values Form values, login values are prefixed with `LOGIN_INPUT_PREFIX`
 * @param {Object} dispatch Redux dispatch function
 * @param {Object} estimaSubmitCallback Estima form submit callback
 */
function updateContactSubmit(values, dispatch, estimaSubmitCallback) {
    const updatedContactValues = extractLoginValues(values);

    return AuthService.updateContact(updatedContactValues)
        .then(() => estimaSubmitCallback())
        .catch((error) => handleAuthError(error, dispatch));
}

/**
 * Submit Barometre form.
 * @param {Object} values Form values, login values are prefixed with `LOGIN_INPUT_PREFIX`
 * @param {Object} dispatch Redux dispatch function
 * @param {Object} estimaSubmitCallback Estima form submit callback
 */
function barometreSubmit(values, dispatch, estimaSubmitCallback) {
    if (values[`${LOGIN_INPUT_PREFIX}action`] === 'update' && !isOfflineWithToken()) {
        return updateContactSubmit(values, dispatch, estimaSubmitCallback);
    }

    return estimaSubmitCallback();
}

/**
 * Submit callback.
 * ⚠️ Redux-form consider any resolved value as an `asyncErrors`. So if everything is fine, you should resolve true.
 * @param {Object} values Form input data
 */
const onSubmit = (values, dispatch, props) => {
    const estimaSubmitCallback = () => estimaSubmit(values, dispatch);

    if (isBarometreStepNumber(values[STEP_NUMBER_NAME])) {
        return barometreSubmit(values, dispatch, estimaSubmitCallback);
    }

    if (isRetrievePhoneStep(values)) {
        return updateContactSubmit(values, dispatch, estimaSubmitCallback);
    }

    const userIsAuthenticated = !isLoginStep(values); // user was authenticated before beginning estima
    const userHasAlreadySignup = props.values[SIGNUP_SUCCEEDED]; // user has done signup in estima, but estima submit has previously failed
    if (userIsAuthenticated || userHasAlreadySignup) {
        return estimaSubmitCallback();
    }
};

/**
 * Determine whether or not validation should proceed.
 * @param {Object} params Updated params
 */
const shouldAsyncValidate = (params) => params.trigger === 'submit';

/**
 * Map state value to props.
 * @param {Object} state Current Redux State
 */
const mapStateToProps = (state) => ({
    auth: state.auth,
    step: state.steps,
    values: state.form[ESTIMA_FORM] && state.form[ESTIMA_FORM].values,
    formErrors: state.form[ESTIMA_FORM] && state.form[ESTIMA_FORM].submitErrors,
    formHints: state.form[ESTIMA_FORM] && state.form[ESTIMA_FORM].hints,
    address: state.address,
});

/**
 * Validation callback for async validation.
 * ⚠️ Redux-form consider any resolved value as an `asyncErrors`. So if everything is fine, you should resolve true.
 * @param {Object} values Form input data
 */
const mapDispatchToProps = (dispatch) => ({
    changeStep: (step) => dispatch(actions.changeStep(step)),
    previousStep: () => dispatch(actions.previousStep()),
    nextStep: () => dispatch(actions.nextStep()),
    goToResult: (id) => dispatch(actions.goToResult(id)),
    stepsReady: () => dispatch(actions.stepsReady()),
    gaLogin: (type) => dispatch(actions.gaLogin(type)),
    getEstima: (id, onFullFill) => dispatch(actions.getEstima(id, onFullFill)),
    setEstima: (estima) => dispatch(actions.getEstimaReceived(estima)),
});

/**
 * App Connected Component.
 */
export default reduxForm({
    form: ESTIMA_FORM,
    destroyOnUnmount: false,
    onSubmit,
    asyncValidate,
    shouldAsyncValidate,
    initialValues: {
        [FORM_ADDRESS_NAME]: '',
        [FORM_CITY_NAME]: '',
        [FORM_ZIP]: '',
        room_count: 1,
        bathroom_count: 1,
        floor: 1,
        floor_count: 1,
        level_count: 0,
        shared_wall_count: 1,
        cellar_count: 1,
        parking_count: 1,
        secondary_building_count: 1,
        secondary_room_count: 1,
        ground_garden: GROUND_GARDEN_OPTIONS[0].value,
        relative_perception: AVERAGE_RELATIVE_PERCEPTION,
        standing: AVERAGE_STANDING,
        lightness: AVERAGE_LIGHTNESS,
        peacefulness: AVERAGE_PEACEFULNESS,
        transport_proximity: AVERAGE_TRANSPORT_PROXIMITY,
        renovation_level: AVERAGE_RENOVATION_LEVEL,
        roof_quality: AVERAGE_ROOF,
        profile_owner: true,
        user_is_pro: false,
        profile_optin_realtors: false, // TODO B2B-647: remove this key/value once backend has cleaned up validate endpoint
        origin: ORIGIN_ESTIMA_FUNNEL,

        /* Step conditionals values */
        _zoneMedia: false,
        _phoneRequired: true, // whether phone is required in signup step or not
        _phoneStepRequired: false, // whether we should display retrieve phone step or not

        /* Login values */
        [`${LOGIN_INPUT_PREFIX}action`]: 'signup',
        [`${LOGIN_INPUT_PREFIX}optin`]: 1,
        [`${LOGIN_INPUT_PREFIX}is_pro`]: '',
        [`${LOGIN_INPUT_PREFIX}profile`]: 'USER_PROFILE.REALTOR',
        [`${LOGIN_INPUT_PREFIX}firstname`]: '',
        [`${LOGIN_INPUT_PREFIX}lastname`]: '',
        [`${LOGIN_INPUT_PREFIX}email`]: '',
        [`${LOGIN_INPUT_PREFIX}cell_phone`]: '',
        [`${LOGIN_INPUT_PREFIX}password`]: '',
        [`${LOGIN_INPUT_PREFIX}optin_partners`]: 0,
        [`${LOGIN_INPUT_PREFIX}username`]: '',
        [`${LOGIN_INPUT_PREFIX}remember_me`]: '',
    },
})(connect(mapStateToProps, mapDispatchToProps)(AppComponent));
